import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import momentTZ from "moment-timezone";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetTimezones } from "api/queries/common/useGetTimezones";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import Textarea from "components/atoms/Textarea/Textarea";
import Alert from "components/molecules/Alert/Alert";
import testIds from "constants/testIds";
import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import StopPlanningSidebar from "./StopPlanningSidebar";
import useScheduleHourNotesHook from "../../hooks/useScheduleHourNotesHook";
import PickupDeliveryDate from "../../PickupDeliveryDate";
import {
  asapDeliveryOption,
  multiStopDeliveryTimeTypeOptions,
  multiStopPickupTimeTypeOptions,
} from "../../ScheduleDefaultOptions";
import useGetMultiStop from "../../hooks/useGetMultiStop";

export default function StopPlanningFormSection({ index }: { index: number }) {
  const intl = useIntl();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const [holidays, setHolidays] = useState<any>({});
  const {
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { allowAsapDelivery } = useGetMultiStop();

  // Only watch fields that need reactive updates for rendering
  const timeType = watch(`schedule.stops.${index}.stop_time_type`);
  const countryId = watch(`schedule.stops.${index}.country_id`);
  const stateName = watch(`schedule.stops.${index}.state_name`);
  const stopType = watch(`schedule.stops.${index}.stop_type`);
  const stopAddressType = watch(`schedule.stops.${index}.stop_address_type`);

  const firstPickupAfterDate = watch(`schedule.stops.0.stop_after_date`);
  const firstPickupAfterTime = watch(`schedule.stops.0.stop_after_time`);

  const isPickup = stopType === StopType.PICKUP;
  const isBeforeStopTimeType = timeType === StopTimeType.BEFORE;
  const isBetweenStopTimeType = timeType === StopTimeType.BETWEEN;
  const isAsapDelivery = timeType === StopTimeType.ASAP_DELIVERY;

  const formattedFirstPickupAfterDateTime = useMemo(
    () => moment(`${firstPickupAfterDate} ${firstPickupAfterTime}`, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm"),
    [firstPickupAfterDate, firstPickupAfterTime],
  );

  const { data: countries = [] } = useGetCountries({
    select: (data) => data.filter((country: { label: string }) => country.label !== "USA"),
    refetchOnMount: false,
  });

  const countryCode = useMemo(
    () => countries?.find((c: any) => c?.id?.toString() === countryId?.toString())?.code,
    [countries, countryId],
  );

  const isAirportOrSeaportStop =
    isOptionalPortAddressFormEnabled &&
    (stopAddressType === StopAddressType.AIRPORT || stopAddressType === StopAddressType.SEAPORT);

  const timezoneQueryFilters = useMemo(() => {
    if (isAirportOrSeaportStop) return {};
    return {
      country_code: countryCode,
      ...(stateName && { state_name: stateName }),
    };
  }, [isAirportOrSeaportStop, countryCode, stateName]);

  const { data: timezones = [] }: any = useGetTimezones(timezoneQueryFilters, {
    enabled: isAirportOrSeaportStop || !!countryCode,
    select: (data: any) => data.map((tz: any) => ({ value: tz.value, label: tz.name })),
  });

  const { showHour, showNotes, handleShowHour, handleShowNotes } = useScheduleHourNotesHook({
    hoursFromKey: `schedule.stops.${index}.stop_hours_start`,
    hoursToKey: `schedule.stops.${index}.stop_hours_end`,
    notesKey: `schedule.stops.${index}.stop_notes`,
  });

  // === VALIDATION START ===
  const checkValidHours = useCallback(
    (value: any): boolean =>
      moment(value, "HH:mm:ss").isAfter(moment(getValues(`schedule.stops.${index}.stop_hours_start`), "HH:mm:ss")),
    [getValues, index],
  );

  const checkValidBeforeDate = useCallback(() => {
    const firstAfterDate = getValues(`schedule.stops.0.stop_after_date`);
    const firstAfterTime = getValues(`schedule.stops.0.stop_after_time`);
    const firstTimezone = getValues(`schedule.stops.0.stop_timezone`);

    const currentBeforeDate = getValues(`schedule.stops.${index}.stop_before_date`);
    const currentBeforeTime = getValues(`schedule.stops.${index}.stop_before_time`);
    const currentTimezone = getValues(`schedule.stops.${index}.stop_timezone`);
    const currentStopType = getValues(`schedule.stops.${index}.stop_type`);

    const currentIsDelivery = currentStopType === StopType.DROPOFF;

    const firstAfterDateTime = momentTZ.tz(`${firstAfterDate} ${firstAfterTime}`, "YYYY-MM-DD HH:mm:ss", firstTimezone);

    const currentBeforeDateTime = momentTZ.tz(
      `${currentBeforeDate} ${currentBeforeTime}`,
      "YYYY-MM-DD HH:mm:ss",
      currentTimezone,
    );

    if (!currentIsDelivery || !firstAfterDateTime.isValid() || !currentBeforeDateTime.isValid()) return true;
    return moment(firstAfterDateTime).isBefore(moment(currentBeforeDateTime));
  }, [getValues, index]);
  // === VALIDATION END ===

  const handleDateSelect = useCallback(
    (date?: Date) => {
      const currentTimeType = getValues(`schedule.stops.${index}.stop_time_type`);
      if (currentTimeType === StopTimeType.AFTER) {
        setValue(`schedule.stops.${index}.stop_before_date`, moment(date).format("YYYY-MM-DD"));
      } else if (currentTimeType === StopTimeType.BEFORE) {
        setValue(`schedule.stops.${index}.stop_after_date`, moment(date).format("YYYY-MM-DD"));
      }
    },
    [index],
  );

  const handleTimeSelect = useCallback(
    (time?: string) => {
      const currentTimeType = getValues(`schedule.stops.${index}.stop_time_type`);
      if (currentTimeType === StopTimeType.AFTER) {
        setValue(`schedule.stops.${index}.stop_before_time`, time);
      } else if (currentTimeType === StopTimeType.BEFORE) {
        setValue(`schedule.stops.${index}.stop_after_time`, time);
      }
    },
    [index],
  );

  const renderDateAndTimeFields = useCallback(
    (dateKey: string, timeKey: string, checkValidDate?: () => boolean, hideValidationError?: boolean) => (
      <>
        <div className="w-192 align-date">
          <div
            data-testid={
              dateKey.includes("before")
                ? `${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_BEFORE_DATE_PREFIX}-${index}`
                : `${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_AFTER_DATE_PREFIX}-${index}`
            }
          >
            <PickupDeliveryDate
              datePickerProps={{
                name: dateKey,
                required: true,
                ...(checkValidDate && {
                  validations: {
                    isValidDeliveryDate: () => checkValidDate(),
                  },
                }),
                hideValidationError,
              }}
              countries={countries}
              holidays={holidays}
              setHolidays={setHolidays}
              countryCode={countryCode}
              onDateSelect={handleDateSelect}
            />
          </div>
        </div>
        <div
          data-testid={
            timeKey.includes("before")
              ? `${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_BEFORE_TIME_PREFIX}-${index}`
              : `${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_AFTER_TIME_PREFIX}-${index}`
          }
        >
          <TimeInput
            name={timeKey}
            required
            registerOptions={{
              deps: [dateKey, timeKey],
            }}
            onTimeSelect={handleTimeSelect}
          />
        </div>
      </>
    ),
    [countries, holidays, index, countryCode],
  );

  const ErrorDates = useCallback(
    () => (
      <>
        {errors?.schedule?.stops?.[index]?.stop_before_date && (
          <>
            {(errors?.schedule?.stops?.[index]?.stop_before_date?.type as any) === "required" ? (
              <div className="d-block font-b3 invalid-feedback">
                <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
              </div>
            ) : (
              <Alert className="w-100 mt-md" variant="danger">
                <FormattedMessage
                  id="new-request.schedule.multistop.error.deliveryAfterPickup"
                  defaultMessage="Delivery schedule should be after {pickupDateTime} (note: timezone is considered as well)"
                  values={{
                    pickupDateTime: formattedFirstPickupAfterDateTime,
                  }}
                />
              </Alert>
            )}
          </>
        )}
      </>
    ),
    [errors?.schedule?.stops, index, formattedFirstPickupAfterDateTime],
  );

  const AfterDateAndTimeFields = useCallback(() => {
    const afterDateKey = `schedule.stops.${index}.stop_after_date`;
    const afterTimeKey = `schedule.stops.${index}.stop_after_time`;

    return renderDateAndTimeFields(afterDateKey, afterTimeKey);
  }, [index, renderDateAndTimeFields]);

  const BeforeDateAndTimeFields = useCallback(() => {
    const dateKey = `schedule.stops.${index}.stop_before_date`;
    const timeKey = `schedule.stops.${index}.stop_before_time`;

    return renderDateAndTimeFields(dateKey, timeKey, checkValidBeforeDate, true);
  }, [index, renderDateAndTimeFields, checkValidBeforeDate]);

  const hoursStartRegisterOptions = useMemo(
    () => ({
      deps: `schedule.stops.${index}.stop_hours_start`,
    }),
    [index],
  );

  const hoursEndValidations = useMemo(
    () => ({
      isValidPickupHours: (v: any) =>
        checkValidHours(v) ||
        intl.formatMessage({
          id: "new-request.schedule.specifyPickupHourErrorMessage",
          defaultMessage: "End time should be greater than start time",
        }),
    }),
    [checkValidHours, intl],
  );

  const deliveryTimeTypeOptions = useMemo(
    () =>
      allowAsapDelivery
        ? [...multiStopDeliveryTimeTypeOptions(intl), ...asapDeliveryOption(intl)]
        : multiStopDeliveryTimeTypeOptions(intl),
    [intl, allowAsapDelivery],
  );

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <StopPlanningSidebar
        index={index}
        showHour={showHour}
        showNotes={showNotes}
        handleShowHour={handleShowHour}
        handleShowNotes={handleShowNotes}
      />

      <div>
        <div className="w-192 dropdown-div mb-lg">
          <Dropdown
            useHookForm
            name={`schedule.stops.${index}.stop_time_type`}
            dataTestid={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_TIME_TYPE_PREFIX}-${index}`}
            options={isPickup ? multiStopPickupTimeTypeOptions(intl) : deliveryTimeTypeOptions}
            required
          />
        </div>

        {!isAsapDelivery && (
          <>
            <div className="flex-row flex-wrap main-wrapper">
              {isBeforeStopTimeType ? <BeforeDateAndTimeFields /> : <AfterDateAndTimeFields />}
              <div className="w-192 timezone dropdown-div">
                <Dropdown
                  useHookForm
                  required
                  name={`schedule.stops.${index}.stop_timezone`}
                  dataTestid={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_TIMEZONE_PREFIX}-${index}`}
                  options={timezones}
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.addressBookDialog.timezone",
                    defaultMessage: "Timezone",
                  })}
                />
              </div>
            </div>

            {isBetweenStopTimeType && (
              <div className="flex-row flex-wrap main-wrapper mt-md">
                <BeforeDateAndTimeFields />
              </div>
            )}

            <ErrorDates />
          </>
        )}

        {showHour && (
          <div className="flex-row">
            <div className="w-87 mt-2">
              <label>
                <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />
              </label>
              <TimeInput
                name={`schedule.stops.${index}.stop_hours_start`}
                defaultValue="09:00:00"
                required
                registerOptions={hoursStartRegisterOptions}
              />
            </div>
            <div className="w-87 mt-2">
              <label>
                <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />
              </label>
              <TimeInput
                name={`schedule.stops.${index}.stop_hours_end`}
                defaultValue="18:00:00"
                required
                validations={hoursEndValidations}
              />
            </div>
          </div>
        )}

        {/* Notes */}
        {showNotes && (
          <Textarea
            name={`schedule.stops.${index}.stop_notes`}
            testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_NOTES_PREFIX}-${index}`}
            placeholder={intl.formatMessage({
              id: `${isPickup ? "new-request.schedule.pickupInstructions" : "new-request.schedule.deliveryInstructions"}`,
              defaultMessage: `${isPickup ? "Add pickup instructions" : "Add delivery instructions"}`,
            })}
            rows={2}
            className="text-body bg-white w-392 mt-3"
          />
        )}
        {/* END */}
      </div>
    </div>
  );
}
